import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
  Tooltip, Typography
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import { PieceJointe } from '../models/PieceJointe';

// Pièces jointes d'un dossier (« Informations du formulaire », admin et admin Legrand) : lien vers chaque fichier et
// corbeille pour retirer un document déposé par erreur, après confirmation. Le serveur le range dans supprimes/
// (récupérable) ; ecard.pdf et visa.pdf, gérés par leurs propres boutons, n'ont pas de corbeille.

interface Props {
  fichiers: PieceJointe[];
  // Appelée après confirmation ; gère elle-même son message de succès ou d'erreur et le rechargement de la liste
  onSupprimer: (piece: PieceJointe) => Promise<void>;
}

const ListePiecesJointes: React.FC<Props> = ({ fichiers, onSupprimer }) => {
  const [piece, setPiece] = useState<PieceJointe | null>(null); // gardée pendant la fermeture du dialogue
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const demander = (p: PieceJointe) => {
    setPiece(p);
    setOuvert(true);
  };

  const confirmer = async () => {
    if (!piece) return;
    setEnCours(true);
    try {
      await onSupprimer(piece);
    } finally {
      setEnCours(false);
      setOuvert(false);
    }
  };

  if (!fichiers.length) {
    return (
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Aucune pièce jointe pour le moment.
      </Typography>
    );
  }

  return (
    <>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {fichiers.map((p) => (
          <Box component="li" key={p.nom} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 28 }}>
            <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{p.nom}</a>
            {p.supprimable && (
              <Tooltip title="Supprimer ce document">
                <IconButton size="small" aria-label={`Supprimer ${p.nom}`} onClick={() => demander(p)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}
      </Box>

      <Dialog open={ouvert} onClose={() => { if (!enCours) setOuvert(false); }}>
        <DialogTitle>Supprimer ce document ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            « {piece ? piece.nom : ''} » sera retiré des pièces jointes du dossier. Une copie reste sur le serveur
            (sous-dossier supprimes/) en cas d'erreur.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOuvert(false)} disabled={enCours}>Annuler</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmer}
            disabled={enCours}
            startIcon={enCours ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ListePiecesJointes;
